import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, retryWhen } from 'rxjs/operators';
import { BedrockRuntimeClient, InvokeModelCommand } from '@aws-sdk/client-bedrock-runtime';

export interface CombinedIqSummaryOptions {
  modelId?: string;
  inferenceConfig?: {
    maxTokens?: number;
    temperature?: number;
    topP?: number;
    stopSequences?: string[];

    // Writer-style aliases the Lambda also accepts
    max_tokens?: number;
    top_p?: number;
    stop?: string[];
    min_tokens?: number;
    seed?: number;
    presence_penalty?: number;
    frequency_penalty?: number;
  };
  additionalModelRequestFields?: Record<string, any>;
  requestMetadata?: Record<string, string>;
}

export interface CombinedIqSummaryResponse {
  summary: string;
  agreements?: string;
  disagreements?: string;
  betterAnswer?: string;
  mostLikelyOutcome?: string;
  confidenceNotes?: string;
  preferredModel?: {
    modelId: string;
    modelName: string;
  } | null;
  includedModels?: Array<{
    modelId: string;
    modelName: string;
  }>;
  omittedModels?: Array<{
    modelId: string;
    modelName: string;
    reason: string;
  }>;
  modelId?: string;
  requestId?: string;
  stopReason?: string;
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeepIQService {
  private readonly sonnet4Monster = 'https://62lyw2efznwpoud7jrdbsntsxu0xzcbn.lambda-url.us-east-1.on.aws/';
  private readonly deepSeekR1IQ = 'https://psteqnbrwatxucfymaocvrfu5i0zfloo.lambda-url.us-east-1.on.aws/';
  private readonly gpt52Monster = 'https://tne7bbeuhmkbkfn4mhgdtuzop40pxazj.lambda-url.us-east-1.on.aws/';
  private readonly metaIQMaverick = 'https://qbun3x5tvfoyma7b7jry2uce6e0uavsn.lambda-url.us-west-2.on.aws/';
  private readonly mistralLargeIQWest = 'https://ywizx4foqvvicgvo5od4bs6i7q0mzlxr.lambda-url.us-west-2.on.aws/';
  private readonly iqSummaryWriterX5LambdaUrl =
    'https://dnpdxprqnoaopgmlozm7gx4lzu0yjwgb.lambda-url.us-east-1.on.aws/';
  private readonly modelId =
    'arn:aws:bedrock:us-west-2:203918849532:inference-profile/us.anthropic.claude-3-5-sonnet-20241022-v2:0';
  private readonly coherePerson = 'https://x7daefznsukybbr5illy67lyvi0cinmr.lambda-url.us-west-2.on.aws/';
  private readonly cohereCommandRPlusIQUrl = 'https://jc667cdb4ac26ft3uwk5vynzay0afnjg.lambda-url.us-west-2.on.aws/';
  private readonly claudePerson = 'https://ze2tdyqjxt2gjallf4ynw4vuyi0jnngh.lambda-url.us-west-2.on.aws/';
  private readonly metaPerson = 'https://urnqgfox3726taftjfvvmm3udm0xzopj.lambda-url.us-west-2.on.aws/';
  private readonly maxRetries = 3;
  private readonly client = new BedrockRuntimeClient({ region: 'us-west-2' });

  constructor(private http: HttpClient) {}

  IQLambdaFunctionClaudius37(prompt: string): Observable<any> {
    return this.http.post(this.sonnet4Monster, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`Sonnet 4 IQ API Error: ${error?.message || error}`)))
    );
  }

  personLambdaFunctionClaudius37(prompt: string): Observable<any> {
    return this.http.post(this.claudePerson, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`Claude IQ API Error: ${error?.message || error}`)))
    );
  }

  monsterGPT52Assessment(prompt: string): Observable<any> {
    return this.http.post(this.gpt52Monster, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`openai IQ API Error: ${error?.message || error}`)))
    );
  }

  mistralMonsterIQAssessment(prompt: string): Observable<any> {
    return this.mistralLargeIQAssessment(prompt);
  }

  /** Legacy `palmx` slot routed to DeepSeek R1. */
  palmxMonsterIQAssessment(prompt: string): Observable<any> {
    return this.seekMonsterIQAssessment(prompt);
  }

  cohereMonsterPersonAssessment(prompt: string): Observable<any> {
    const requestBody = {
      prompt: prompt ?? ''
    };

    return this.http.post<any>(this.cohereCommandRPlusIQUrl, requestBody).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error: HttpErrorResponse) => {
        console.error('Error in Cohere Command R+ IQ call:', error);

        const message =
          error?.error?.error ||
          error?.error?.message ||
          error?.message ||
          'Unknown error';

        return throwError(() => new Error(`Cohere Command R+ IQ API Error: ${message}`));
      })
    );
  }

  seekMonsterIQAssessment(prompt: string): Observable<any> {
    return this.http.post<any>(this.deepSeekR1IQ, { prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error: any) => {
        const message = error?.error?.error || error?.error?.message || error?.message || String(error);
        return throwError(() => new Error(`DeepSeek R1 IQ API Error: ${message}`));
      })
    );
  }

  maverickIQAssessment(prompt: string): Observable<any> {
    return this.http.post<any>(this.metaIQMaverick, { prompt: prompt ?? '' }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error: HttpErrorResponse) => {
        const message = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        return throwError(() => new Error(`Meta Maverick IQ API Error: ${message}`));
      })
    );
  }

  claude35IQAssessment(prompt: string): Observable<any> {
    return this.http.post(this.sonnet4Monster, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`Sonnet 4 IQ API Error: ${error?.message || error}`)))
    );
  }

  llama33IQAssessment(prompt: string): Observable<any> {
    return this.seekMonsterIQAssessment(prompt);
  }

  mistralLargeIQAssessment(prompt: string): Observable<any> {
    return this.http.post(this.mistralLargeIQWest, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`Mistral Large IQ API Error: ${error?.message || error}`)))
    );
  }

  metaManPersonAssessment(prompt: string): Observable<any> {
    return this.http.post(this.metaPerson, { userprompt: prompt }).pipe(
      retryWhen((errors) => this.retryStrategy(errors)),
      catchError((error) => throwError(() => new Error(`Meta IQ API Error: ${error?.message || error}`)))
    );
  }

  callCombinedSummary(
    prompt: string,
    data: Record<string, any>,
    options?: CombinedIqSummaryOptions
  ): Observable<CombinedIqSummaryResponse> {
    const body: Record<string, any> = {
      prompt: prompt ?? '',
      data: data ?? {}
    };

    // Let the Lambda default its own model unless you intentionally override it.
    if (options?.modelId) {
      body.modelId = options.modelId;
    }

    if (options?.inferenceConfig) {
      body.inferenceConfig = options.inferenceConfig;
    }

    if (options?.additionalModelRequestFields) {
      body.additionalModelRequestFields = options.additionalModelRequestFields;
    }

    if (options?.requestMetadata) {
      body.requestMetadata = options.requestMetadata;
    }

    return this.http
      .post<CombinedIqSummaryResponse>(
        this.iqSummaryWriterX5LambdaUrl,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        retryWhen((errors) => this.retryStrategy(errors)),
        catchError((error: HttpErrorResponse) => {
          console.error('Error in IQ combined summary (Writer X5) lambda call:', error);

          const message =
            error?.error?.error ||
            error?.error?.message ||
            error?.message ||
            String(error);

          return throwError(() => new Error(`Summary Lambda Error: ${message}`));
        })
      );
  }

  getMultiModelSummary(prompt: string, data: any): Observable<string> {
    const messages = [{ role: 'user', content: `\n\nHuman: Given the data: ${JSON.stringify(data)}, ${prompt}\n\nAssistant:` }];
    const requestParams = {
      modelId: this.modelId,
      contentType: 'application/json',
      accept: 'application/json',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': 'bedrock-2023-05-31'
      },
      body: JSON.stringify({
        anthropic_version: 'bedrock-2023-05-31',
        messages,
        max_tokens: 1024
      })
    };

    return from(this.client.send(new InvokeModelCommand(requestParams))).pipe(
      map((response) => {
        const responseBody = response.body ? new TextDecoder().decode(response.body) : '{}';
        const parsedResponse = JSON.parse(responseBody);
        if (parsedResponse?.content?.length > 0) {
          return parsedResponse.content[0].text as string;
        }
        throw new Error('Content is missing in the response');
      }),
      catchError((error) => throwError(() => new Error(`Bedrock API Error: ${error?.message || error}`))),
      retryWhen((errors) => this.retryStrategy(errors))
    );
  }

  private retryStrategy(errors: Observable<HttpErrorResponse>): Observable<number> {
    const maxRetries = 2;

    return errors.pipe(
      mergeMap((error: HttpErrorResponse, index: number) => {
        const attempt = index + 1;
        const status = error?.status ?? 0;
        const isRetryable = status === 0 || status >= 500;

        if (attempt > maxRetries || !isRetryable) {
          return throwError(() => error);
        }

        return timer(attempt * 1000);
      })
    );
  }
}
